import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Input,
  Button,
  Textarea,
  useBreakpointValue,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import colors from "../../../utils/colors";
import {
  useGetCategoriesByGroupName,
  useGetSymptomsByGroupAndCategory,
} from "../../../apis/patients";

interface AppointmentComplaintsCardProps {
  title?: string;
  patientGender?: "male" | "female";
  pregnancyStatus?: "Yes" | "No" | "Not sure" | "";
  onValidationChange?: (isValid: boolean) => void;
  onPregnancyStatusChange?: (status: "Yes" | "No" | "Not sure") => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  appointmentId: string;
  patientAge?: string;
  currentOtherComplaint?: number;
  formik?: any;
  bodyParts: any;
  loadingBodyParts: boolean;
}

const AppointmentComplaintsCard: React.FC<AppointmentComplaintsCardProps> = ({
  title = "Main complaint",
  patientGender,
  pregnancyStatus,
  onPregnancyStatusChange,
  onValidationChange,
  currentOtherComplaint = 0,
  formik,
  loadingBodyParts,
  bodyParts,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredSymptoms, setFilteredSymptoms] = useState<string[]>([]);

  const bodyPartsArray = Array.isArray(bodyParts) ? bodyParts : [];

  const groupName =
    bodyPartsArray.find(
      (part) =>
        part.name ===
        (title === "Main complaint"
          ? formik?.values?.mainComplaint?.selectedBodyPart
          : formik?.values?.otherComplaints?.[currentOtherComplaint]
              ?.selectedBodyPart),
    )?.[title === "Main complaint" ? "main" : "other"] || "";
  const { data: categories, isLoading: loadingCategories } =
    useGetCategoriesByGroupName(groupName);
  const { data: symptoms, isLoading: loadingSymptoms } =
    useGetSymptomsByGroupAndCategory(
      groupName,
      title === "Main complaint"
        ? formik?.values?.mainComplaint?.selectedComplaint
        : formik?.values?.otherComplaints?.[currentOtherComplaint]
            ?.selectedComplaint,
    );

  useEffect(() => {
    if (title === "Main complaint") {
      const isValid =
        !!formik?.values?.mainComplaint?.selectedBodyPart &&
        !!formik?.values?.mainComplaint?.selectedComplaint &&
        !!formik?.values?.mainComplaint?.selectedSymptom;
      onValidationChange?.(isValid);
    }
  }, [
    title,
    formik?.values?.mainComplaint?.selectedBodyPart,
    formik?.values?.mainComplaint?.selectedComplaint,
    formik?.values?.mainComplaint?.selectedSymptom,
    onValidationChange,
    formik?.errors,
  ]);

  //const symptomsPerPage = 5;
  //const symptomsPerPage = useBreakpointValue({ base: 3, md: 5 });
  const symptomsPerPage = useBreakpointValue({ base: 3, md: 5 }) ?? 5;

  useEffect(() => {
    const symptomsArray = Array.isArray(symptoms) ? symptoms : [];
    const filtered = symptomsArray.filter((symptom: string) =>
      symptom.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredSymptoms(filtered);
  }, [symptoms, searchTerm]);
  const totalPages = Math.ceil(filteredSymptoms.length / symptomsPerPage);
  const displayedSymptoms = filteredSymptoms.slice(
    (currentPage - 1) * symptomsPerPage,
    currentPage * symptomsPerPage,
  );

  const filteredBodyParts =
    bodyPartsArray.filter((bodyPart: any) => {
      if (patientGender?.toLowerCase() === "male")
        return bodyPart.name !== "Women Reproductive System";
      if (patientGender?.toLowerCase() === "female")
        return bodyPart.name !== "MEN Reproductive System";
      return true;
    }) || [];
  const selectedSymptom =
    title === "Main complaint"
      ? formik?.values?.mainComplaint?.selectedSymptom
      : formik?.values?.otherComplaints[currentOtherComplaint]?.selectedSymptom;

  const note =
    title === "Main complaint"
      ? formik?.values?.mainComplaint?.note
      : formik?.values?.otherComplaints[currentOtherComplaint]?.note;

  const getError = (property) => {
    if (title === "Main complaint") {
      if (!!formik?.errors?.mainComplaint?.[property]) {
        return (
          <Text
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "red",
              marginTop: "10px",
            }}
          >
            {formik?.errors?.mainComplaint[property]}
          </Text>
        );
      }
    } else {
      if (
        !!formik?.errors?.otherComplaints?.[currentOtherComplaint]?.[property]
      ) {
        return (
          <Text
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "red",
              marginTop: "10px",
            }}
          >
            {formik?.errors?.otherComplaints[currentOtherComplaint][property]}
          </Text>
        );
      }
    }
  };
  return (
    <Flex
      backgroundColor="white"
      borderRadius="8px"
      gap="20px"
      boxShadow="1px 4px 15px 0px #3030301F"
      width="100%"
      direction="column"
      padding="40px"
    >
      <Text
        fontFamily="lato"
        fontSize="16px"
        fontWeight={700}
        color={colors.dark}
      >
        {title}
      </Text>

      <Box>
        <Text
          fontFamily="lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
          mb="12px"
        >
          Body part
        </Text>
        <Select
          placeholder="Select body part"
          focusBorderColor={colors.primary}
          onChange={(e) => {
            const value = e.target.value;
            if (title === "Main complaint") {
              formik?.setFieldValue("mainComplaint", {
                ...formik?.values?.mainComplaint,
                selectedBodyPart: value,
                selectedComplaint: "",
                selectedSymptom: "",
              });
            } else {
              const otherComp = formik?.values?.otherComplaints;
              otherComp[currentOtherComplaint] = {
                ...otherComp[currentOtherComplaint],
                selectedBodyPart: value,
                selectedComplaint: "",
                selectedSymptom: "",
              };

              formik?.setFieldValue("otherComplaints", otherComp);
            }
            setSearchTerm("");
            setCurrentPage(1);
          }}
          value={
            title === "Main complaint"
              ? formik?.values?.mainComplaint?.selectedBodyPart
              : formik?.values?.otherComplaints[currentOtherComplaint]
                  ?.selectedBodyPart
          }
        >
          {loadingBodyParts ? (
            <option disabled>Loading...</option>
          ) : (
            filteredBodyParts.map((part: any) => (
              <option key={part.main} value={part.name}>
                {part.name}
              </option>
            ))
          )}
        </Select>
        {getError("selectedBodyPart")}
      </Box>

      {formik?.values?.mainComplaint?.selectedBodyPart ===
        "Women Reproductive System" &&
        title === "Main complaint" && (
          <Box>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Are you pregnant?
            </Text>
            <RadioGroup
              onChange={(value) =>
                onPregnancyStatusChange?.(value as "Yes" | "No" | "Not sure")
              }
              value={pregnancyStatus}
            >
              <Stack direction="row" spacing={5}>
                <Radio value="Yes" colorScheme="blue">
                  Yes
                </Radio>
                <Radio value="No" colorScheme="blue">
                  No
                </Radio>
                <Radio value="Not sure" colorScheme="blue">
                  Not sure
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
        )}

      <Box>
        <Text
          fontFamily="lato"
          fontSize="14px"
          fontWeight={600}
          color={colors.dark}
          mb="12px"
        >
          Complaint
        </Text>
        {groupName && (
          <Select
            placeholder="Select complaint category"
            focusBorderColor={colors.primary}
            onChange={(e) => {
              const value = e.target.value;
              if (title === "Main complaint") {
                formik?.setFieldValue("mainComplaint", {
                  ...formik?.values?.mainComplaint,
                  selectedComplaint: value,
                  selectedSymptom: "",
                });
              } else {
                const otherComp = formik?.values?.otherComplaints;
                otherComp[currentOtherComplaint] = {
                  ...otherComp[currentOtherComplaint],
                  selectedComplaint: value,
                  selectedSymptom: "",
                };

                formik?.setFieldValue("otherComplaints", otherComp);
              }

              setSearchTerm("");
              setCurrentPage(1);
            }}
            value={
              title === "Main complaint"
                ? formik?.values?.mainComplaint?.selectedComplaint
                : formik?.values?.otherComplaints[currentOtherComplaint]
                    ?.selectedComplaint
            }
          >
            {loadingCategories ? (
              <option disabled>Loading...</option>
            ) : (
              categories?.map((category: any) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))
            )}
          </Select>
        )}
        {getError("selectedComplaint")}
      </Box>

      {(formik?.values?.mainComplaint?.selectedComplaint ||
        formik?.values?.otherComplaints[currentOtherComplaint]
          ?.selectedComplaint) && (
        <>
          <Accordion allowMultiple>
            <AccordionItem>
              <AccordionButton>
                <Box
                  flex="1"
                  textAlign="left"
                  fontWeight={600}
                  color={colors.dark}
                  fontSize={{ base: "14px", md: "16px" }}
                >
                  <Box as="span" fontWeight={600}>
                    Symptom:
                  </Box>{" "}
                  <Box as="span" fontWeight={400}>
                    {selectedSymptom || "None selected"}
                  </Box>
                </Box>
              </AccordionButton>
              <AccordionPanel pb={4} overflowY="auto">
                <Input
                  placeholder="Search symptoms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  mb={4}
                  focusBorderColor={colors.primary}
                />

                {loadingSymptoms ? (
                  <Text>Loading symptoms...</Text>
                ) : (
                  <Box maxW="100%" overflowX="hidden">
                    <Wrap spacing="8px" mb={4} justify="flex-start">
                      {displayedSymptoms.map(
                        (symptom: string, index: number) => (
                          <WrapItem key={index}>
                            <Button
                              type="submit"
                              size="sm"
                              px={3}
                              py={2}
                              fontSize={{ base: "12px", md: "14px" }}
                              minW="100px"
                              maxW="150px"
                              h="auto"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              textAlign="left"
                              display="inline-block"
                              overflowWrap="break-word"
                              variant={
                                selectedSymptom === symptom
                                  ? "solid"
                                  : "outline"
                              }
                              colorScheme="green"
                              onClick={() => {
                                if (title === "Main complaint") {
                                  formik?.setFieldValue("mainComplaint", {
                                    ...formik?.values?.mainComplaint,
                                    selectedSymptom: symptom,
                                  });
                                } else {
                                  const otherComp =
                                    formik?.values?.otherComplaints;
                                  otherComp[currentOtherComplaint] = {
                                    ...otherComp[currentOtherComplaint],
                                    selectedSymptom: symptom,
                                  };
                                  formik?.setFieldValue(
                                    "otherComplaints",
                                    otherComp,
                                  );
                                }
                              }}
                            >
                              {symptom}
                            </Button>
                          </WrapItem>
                        ),
                      )}
                    </Wrap>
                  </Box>
                )}

                <Flex justify="center" gap="2" mt={4}>
                  <Button
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    isDisabled={currentPage === 1}
                  >
                    Prev
                  </Button>
                  <Text alignSelf="center">
                    Page {currentPage} of {totalPages}
                  </Text>
                  <Button
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    isDisabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
            {getError("selectedSymptom")}
          </Accordion>
        </>
      )}

      <Box>
        <Text
          fontFamily="lato"
          fontSize="14px"
          fontWeight={600}
          mb="12px"
          color={colors.dark}
        >
          Note
        </Text>
        <Textarea
          focusBorderColor={colors.primary}
          placeholder="Enter any additional notes"
          onChange={(e) => {
            if (title === "Main complaint") {
              formik?.setFieldValue("mainComplaint", {
                ...formik?.values?.mainComplaint,

                note: e?.target?.value,
              });
            } else {
              const otherComp = formik?.values?.otherComplaints;
              otherComp[currentOtherComplaint] = {
                ...otherComp[currentOtherComplaint],
                note: e?.target?.value,
              };

              formik?.setFieldValue("otherComplaints", otherComp);
            }
          }}
          value={note}
        />

        {getError("note")}
      </Box>
    </Flex>
  );
};

export default AppointmentComplaintsCard;
